import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { TeamPlanConfirmModal } from '@components/Modals/TeamPlanConfirmModal';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import { AnnualBillingSection } from '@components/shared/AnnualBillingSection/AnnualBillingSection.web';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { SubscriptionRightPanel } from '@components/shared/SubscriptionRightPanel/SubscriptionRightPanel.web';
import { TeamSummaryRow } from '@components/shared/TeamSummaryRow/TeamSummaryRow.web';
import { Team } from '@graphql/generated';
import { BillingCycle } from '@root/types';
import theme from '@themes/theme';

const PLAN_OPTIONS = [
  {
    key: 'year' as BillingCycle,
    priceType: 'Yearly',
    savePercentage: 20,
    priceCost: 16,
    isYearly: true,
  },
  {
    key: 'month' as BillingCycle,
    priceType: 'Monthly',
    savePercentage: 0,
    priceCost: 20,
    isYearly: false,
  },
];

interface AnnualConfirmModalBaseProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  team?: Team;
  teamName?: string;
  memberCount: number;
  showBillingCycleSelector?: boolean;
  onContinueToCheckout?: (billingCycle: BillingCycle) => void;
  buttonText?: string;
  title?: string;
  subtitle?: string;
  renderBottomContent?: (
    selectedPlan: BillingCycle,
    memberCount: number
  ) => React.ReactNode;
}

const AnnualConfirmModalBase: React.FC<AnnualConfirmModalBaseProps> = ({
  visible,
  onClose,
  onConfirm,
  team,
  teamName,
  memberCount,
  showBillingCycleSelector = false,
  onContinueToCheckout,
  buttonText,
  title,
  subtitle,
  renderBottomContent,
}) => {
  const { t } = useTranslation();
  const [expandedList, setExpandedList] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<BillingCycle>('year');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const modalTitle = title ?? t('models:teams.subscriptions.upgradeToTeamPlan');
  const modalSubtitle =
    subtitle ?? t('models:teams.subscriptions.powerUpCollab');

  const isOnTrialTeam =
    team?.subscriptionDetails?.isSubscriptionOnTrial === true;
  const xButtonText = isOnTrialTeam
    ? t('models:teams.subscriptions.startYourFreeTrial')
    : buttonText;

  const handleCloseButtonPress = () => {
    setShowCloseConfirm(true);
  };
  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  const renderPlanOption = (plan: (typeof PLAN_OPTIONS)[0]) => {
    const isSelected = selectedPlan === plan.key;

    return (
      <Box flex={1} flexDirection='row' padding='xs'>
        <Box flexDirection='column'>
          <Text variant='webSmall' color='textSecondary' numberOfLines={1}>
            {plan.priceType}
          </Text>
          <Box flexDirection='row'>
            <Text
              marginTop='xs'
              variant='webBodySecondary'
              color='textSecondary'
              numberOfLines={1}>
              {'$' + plan.priceCost + ' /member/month'}
            </Text>
            {plan.isYearly && (
              <Box
                marginHorizontal='xs'
                marginTop='xxxs'
                style={{
                  backgroundColor: '#DCF2EC',
                  borderRadius: 4,
                  padding: 4,
                }}>
                <Text variant='metadata' color='greenSecondary'>
                  {`Save ${plan.savePercentage}%`}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
        {isSelected && (
          <Box position='absolute' right={20} top={17}>
            <Icon name='Check' variant='l' color='greenSecondary' />
          </Box>
        )}
      </Box>
    );
  };

  const trialStr = team?.subscriptionDetails?.trialEndsAt
    ? dayjs(team?.subscriptionDetails?.trialEndsAt).format('MMM D, YYYY')
    : ' - ';
  return (
    <WebModal
      accessibilityLabel='Confirm-Modal'
      visible={visible}
      onClose={onClose}
      height={621}
      width={800}>
      <Box flex={1} flexDirection='row'>
        <Box flex={1} marginStart='l' marginEnd='l' flexDirection='column'>
          <Box marginTop='l'>
            <Text variant='labelEmphasized' style={{ lineHeight: 21 }}>
              {isOnTrialTeam
                ? t(
                    'models:teams.subscriptions.startYourFree30DayTeamPlanTrial'
                  )
                : modalTitle}
            </Text>
          </Box>
          <Box marginTop='xxs'>
            <Text
              variant='webBodyHintSubline'
              style={{ color: theme.colors.grey04 }}>
              {modalSubtitle}
            </Text>
          </Box>

          <TeamSummaryRow team={team} teamName={teamName} />

          {showBillingCycleSelector ? (
            <>
              <Box mb='m'>
                <Text mb='xs' variant='labelSmall' color='textPrimary'>
                  {t('models:teams.subscriptions.billingCycle', {
                    defaultValue: 'Billing Cycle',
                  })}
                </Text>
                {PLAN_OPTIONS.map((plan, index) => {
                  const isVisible = index === 0 || expandedList;
                  if (!isVisible) return null;

                  return (
                    <TouchableOpacity
                      key={plan.key}
                      onPress={() => {
                        setSelectedPlan(plan.key);
                      }}>
                      <Box
                        flexDirection='row'
                        marginTop={index > 0 ? 's' : undefined}
                        borderColor={
                          selectedPlan === plan.key
                            ? 'greenSecondary'
                            : 'grey04'
                        }
                        backgroundColor='white'
                        borderWidth={selectedPlan === plan.key ? 2 : 1}
                        borderRadius='s'>
                        {renderPlanOption(plan)}
                      </Box>
                    </TouchableOpacity>
                  );
                })}
                <TouchableOpacity
                  onPress={() => {
                    setExpandedList((prev) => !prev);
                  }}>
                  <Box flexDirection='row' marginTop='xs' alignItems='center'>
                    <Text
                      variant='webBodySecondary'
                      color='grey04'
                      numberOfLines={1}>
                      {expandedList ? 'view less' : 'view more'}
                    </Text>
                    <Icon
                      marginStart='xxs'
                      color='grey04'
                      name={expandedList ? 'ChevronUp' : 'ChevronDown'}
                      height={16}
                      width={16}
                    />
                  </Box>
                </TouchableOpacity>
              </Box>

              <Box flex={1} position='absolute' bottom={24} left={0} right={0}>
                {renderBottomContent ? (
                  renderBottomContent(selectedPlan, memberCount)
                ) : (
                  <Box flexDirection='column'>
                    <Box flex={1} flexDirection='row' mb='xs'>
                      <Text variant='webBodySecondary' color='textSecondary'>
                        {isOnTrialTeam
                          ? t(
                              'models:teams.subscriptions.youWontBeChargedUntil'
                            )
                          : t('models:teams.subscriptions.youllBeCharged', {
                              defaultValue: "You'll be charged ",
                            })}
                      </Text>
                      <Text variant='bodyTaskName' color='textPrimary'>
                        {isOnTrialTeam
                          ? trialStr
                          : (() => {
                              const selectedPlanOption = PLAN_OPTIONS.find(
                                (plan) => plan.key === selectedPlan
                              );

                              const costPerMember =
                                selectedPlanOption?.priceCost || 0;
                              const totalCost =
                                memberCount *
                                (selectedPlan === 'year'
                                  ? costPerMember * 12
                                  : costPerMember);
                              const period =
                                selectedPlan === 'year'
                                  ? ' per year'
                                  : ' per month';
                              return `$${totalCost}${period}.`;
                            })()}
                      </Text>
                    </Box>
                    <Text
                      mb='l'
                      variant='webBodyHintSubline'
                      color='textSecondary'>
                      {t('models:teams.subscriptions.applicableTaxes', {
                        defaultValue:
                          'Applicable taxes will be calculated on the checkout',
                      })}
                    </Text>
                  </Box>
                )}
                <Button
                  isSmall={true}
                  height={48}
                  fullWidth={true}
                  borderWidth={0}
                  btnTextColor='white'
                  backgroundColor='greenSecondary'
                  variant='primary'
                  onPress={() => {
                    if (onContinueToCheckout) {
                      onContinueToCheckout(selectedPlan);
                    }
                    onClose();
                  }}>
                  {xButtonText ||
                    t('shared:confirm', { defaultValue: 'Confirm' })}
                </Button>
              </Box>
            </>
          ) : (
            <AnnualBillingSection
              memberCount={memberCount}
              onConfirm={() => {
                onConfirm?.();
              }}
              buttonText={buttonText}
            />
          )}
        </Box>
        <SubscriptionRightPanel onClose={handleCloseButtonPress} />
      </Box>
      <TeamPlanConfirmModal
        showModal={showCloseConfirm}
        onClose={handleConfirmClose}
        onPress={handleCancelClose}
        buttonText={t('shared:continue')}
        title={t('shared:subscriptionConfirmTitle')}
        message={t('shared:subscriptionConfirm')}
        cancelButtonText={t('shared:later')}
      />
    </WebModal>
  );
};

interface EditTeamConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  team?: Team;
  teamName?: string;
  memberCount?: number;
}

export const EditTeamConfirmModal: React.FC<EditTeamConfirmModalProps> = ({
  team,
  memberCount = 1,
  ...props
}) => {
  return (
    <AnnualConfirmModalBase
      {...props}
      team={team}
      memberCount={team?.usersCount ?? memberCount}
      buttonText='Confirm Changes'
    />
  );
};

interface UpgradeConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onContinueToCheckout: (billingCycle: BillingCycle) => void;
  team?: Team;
  teamName?: string;
  memberCount?: number;
}

export const UpgradeConfirmModal: React.FC<UpgradeConfirmModalProps> = ({
  memberCount = 1,
  onContinueToCheckout,
  ...props
}) => {
  return (
    <AnnualConfirmModalBase
      {...props}
      memberCount={memberCount}
      showBillingCycleSelector={true}
      onContinueToCheckout={onContinueToCheckout}
      onConfirm={() => {}}
      buttonText='Continue to Checkout'
    />
  );
};

interface TrialConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onStartTrial: (billingCycle: BillingCycle) => void;
  team?: Team;
  teamName?: string;
  memberCount?: number;
}

export const TrialConfirmModal: React.FC<TrialConfirmModalProps> = ({
  memberCount = 1,
  onStartTrial,
  ...props
}) => {
  const { t } = useTranslation();
  const trialEndDate = dayjs().add(30, 'day').format('MMM D, YYYY');

  return (
    <AnnualConfirmModalBase
      {...props}
      memberCount={memberCount}
      showBillingCycleSelector={true}
      onContinueToCheckout={onStartTrial}
      onConfirm={() => {}}
      title={t('models:teams.subscriptions.startYourFree30DayTeamPlanTrial')}
      subtitle={t(
        'models:teams.subscriptions.unlockPowerfulFeaturesTrialSubtitle'
      )}
      buttonText={t('models:teams.subscriptions.startYourFreeTrial')}
      renderBottomContent={() => (
        <Box flexDirection='row' flexWrap='wrap' mb='m'>
          <Text variant='webBodySecondary' color='textSecondary'>
            {t('models:teams.subscriptions.youWontBeChargedUntil')}
          </Text>
          <Text variant='bodyTaskName' color='textPrimary'>
            {trialEndDate}
          </Text>
        </Box>
      )}
    />
  );
};
